import React, { useEffect, useState } from 'react';
import { Modal, Tabs, Table, Tag, Spin, Empty, Form, Select, InputNumber, Button, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { malzemelerAPI, malzemeBirimDonusumleriAPI } from '../services/api';
import { useLookups } from '../hooks/useLookups';
import { formatPara, formatSayi } from '../utils/format';
import dayjs from 'dayjs';

const alisColumns = [
  {
    title: 'Tarih',
    dataIndex: 'tarih',
    key: 'tarih',
    width: 110,
    render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '-',
  },
  {
    title: 'Fatura No',
    dataIndex: 'fatura_no',
    key: 'fatura_no',
    width: 110,
  },
  {
    title: 'Cari',
    dataIndex: 'cari_ad',
    key: 'cari_ad',
    width: 140,
    ellipsis: true,
  },
  {
    title: 'Gider Tipi',
    dataIndex: 'gider_tipi',
    key: 'gider_tipi',
    width: 120,
  },
  {
    title: 'Miktar',
    dataIndex: 'miktar',
    key: 'miktar',
    width: 90,
    align: 'right',
    render: (v) => v != null ? formatSayi(v, 2) : '-',
  },
  {
    title: 'Birim',
    dataIndex: 'birim',
    key: 'birim',
    width: 70,
  },
  {
    title: 'Birim Fiyat',
    dataIndex: 'birim_fiyat',
    key: 'birim_fiyat',
    width: 100,
    align: 'right',
    render: (v) => v != null ? formatPara(v) : '-',
  },
  {
    title: 'Tutar',
    dataIndex: 'tutar',
    key: 'tutar',
    width: 100,
    align: 'right',
    render: (v) => v != null ? formatPara(v) : '-',
  },
  {
    title: 'KDV Dahil',
    dataIndex: 'kdv_dahil_tutar',
    key: 'kdv_dahil_tutar',
    width: 100,
    align: 'right',
    render: (v) => v != null ? formatPara(v) : '-',
  },
  {
    title: 'Açıklama',
    dataIndex: 'aciklama',
    key: 'aciklama',
    ellipsis: true,
  },
];

function MalzemeDetayModal({ open, malzeme, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [alisData, setAlisData] = useState([]);
  const [donusumData, setDonusumData] = useState([]);
  const [donusumLoading, setDonusumLoading] = useState(false);
  const [form] = Form.useForm();
  const { birimOptions: birimler, birimler: birimlerRaw } = useLookups();

  const fetchDonusumler = async () => {
    if (!malzeme?.id) return;
    setDonusumLoading(true);
    try {
      const res = await malzemeBirimDonusumleriAPI.getByMalzeme(malzeme.id);
      setDonusumData(res.data);
    } catch {
      setDonusumData([]);
    } finally {
      setDonusumLoading(false);
    }
  };

  useEffect(() => {
    if (open && malzeme?.id) {
      setLoading(true);
      malzemelerAPI.getDetay(malzeme.id)
        .then((res) => {
          setAlisData(res.data.alis_kalemleri || []);
        })
        .catch(() => {
          setAlisData([]);
        })
        .finally(() => setLoading(false));
      fetchDonusumler();
      form.resetFields();
    } else {
      setAlisData([]);
      setDonusumData([]);
    }
  }, [open, malzeme?.id]);

  const idByAd = Object.fromEntries((birimlerRaw || []).map(b => [b.ad, b.id]));
  const adById = Object.fromEntries((birimlerRaw || []).map(b => [b.id, b.ad]));
  const donusumGruplari = [
    [idByAd['kg'], idByAd['gr']].filter(Boolean),
    [idByAd['lt'], idByAd['ml']].filter(Boolean),
  ];
  const malzemeBirimId = malzeme?.birim_id;
  const malzemeBirimGrup = donusumGruplari.find(g => g.includes(malzemeBirimId)) || [malzemeBirimId];
  const kaynakBirimOptions = birimler;
  const hedefBirimOptions = birimler;

  const handleDonusumEkle = async (values) => {
    let { kaynak_birim_id, hedef_birim_id, carpan } = values;
    const kaynakGrupta = malzemeBirimGrup.includes(kaynak_birim_id);
    const hedefGrupta = malzemeBirimGrup.includes(hedef_birim_id);
    if (kaynakGrupta === hedefGrupta) {
      message.error('Dönüşümün bir tarafı malzemenin birimi (ya da eş grubu) olmalı, diğer taraf farklı bir birim olmalı.');
      return;
    }
    if (kaynakGrupta && !hedefGrupta) {
      [kaynak_birim_id, hedef_birim_id] = [hedef_birim_id, kaynak_birim_id];
      carpan = 1 / Number(carpan);
      message.info(`Kaydedildi: 1 ${adById[kaynak_birim_id]} = ${formatSayi(carpan, 2)} ${adById[hedef_birim_id]}`);
    }
    try {
      await malzemeBirimDonusumleriAPI.create({
        malzeme_id: malzeme.id,
        kaynak_birim_id,
        hedef_birim_id,
        carpan,
      });
      message.success('Dönüşüm eklendi');
      form.resetFields();
      fetchDonusumler();
      if (onSaved) onSaved();
    } catch (err) {
      message.error('Dönüşüm eklenemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const sonDonusumMap = {};
  donusumData.forEach(d => {
    const key = `${d.kaynak_birim_id}-${d.hedef_birim_id}`;
    if (!sonDonusumMap[key] || new Date(d.tarih) > new Date(sonDonusumMap[key].tarih)) {
      sonDonusumMap[key] = d;
    }
  });
  const sonIds = new Set(Object.values(sonDonusumMap).map(d => d.id));

  const donusumColumns = [
    { title: 'Kaynak', dataIndex: 'kaynak_birim', key: 'kaynak_birim', width: 80 },
    {
      title: 'Dönüşüm',
      key: 'donusum',
      render: (_, r) => `1 ${r.kaynak_birim} = ${formatSayi(r.carpan, 2)} ${r.hedef_birim}`,
    },
    {
      title: 'Tarih',
      dataIndex: 'tarih',
      key: 'tarih',
      width: 140,
      render: (v) => v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '-',
    },
    {
      title: 'Durum',
      key: 'durum',
      width: 80,
      render: (_, r) => sonIds.has(r.id) ? <Tag color="green">Aktif</Tag> : <Tag>Eski</Tag>,
    },
  ];

  const tabItems = [
    {
      key: 'alis',
      label: `Alış Geçmişi (${alisData.length})`,
      children: (
        <Table
          columns={alisColumns}
          dataSource={alisData}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
          locale={{ emptyText: <Empty description="Bu malzeme için henüz alış kaydı yok" /> }}
          scroll={{ x: 900 }}
        />
      ),
    },
    {
      key: 'donusum',
      label: `Birim Dönüşümleri (${donusumData.length})`,
      children: (
        <>
          <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 6 }}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Yeni Dönüşüm Ekle</div>
            <div style={{ marginBottom: 8, fontSize: 12, color: '#888' }}>Taraflardan biri malzemenin birimi (veya eş grubu — kg/gr, lt/ml) olmalı. İstediğiniz yönde girebilirsiniz, ters girilirse sistem kanonik biçime çevirir.</div>
            <Form form={form} layout="inline" onFinish={handleDonusumEkle}>
              <span style={{ lineHeight: '32px', marginRight: 4 }}>1</span>
              <Form.Item name="kaynak_birim_id" rules={[{ required: true, message: 'Seçin' }]}>
                <Select placeholder="Birim" options={kaynakBirimOptions} style={{ width: 100 }} />
              </Form.Item>
              <span style={{ lineHeight: '32px', margin: '0 4px' }}>=</span>
              <Form.Item name="carpan" rules={[{ required: true, message: 'Girin' }]}>
                <InputNumber placeholder="Miktar" min={0.0001} step={0.1} style={{ width: 100 }} />
              </Form.Item>
              <Form.Item name="hedef_birim_id" rules={[{ required: true, message: 'Seçin' }]} initialValue={malzemeBirimGrup[malzemeBirimGrup.length - 1]}>
                <Select placeholder="Birim" options={hedefBirimOptions} style={{ width: 80 }} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>Ekle</Button>
              </Form.Item>
            </Form>
          </div>
          <Table
            columns={donusumColumns}
            dataSource={donusumData}
            rowKey="id"
            size="small"
            loading={donusumLoading}
            pagination={false}
            locale={{ emptyText: <Empty description="Henüz dönüşüm tanımlanmamış" /> }}
          />
        </>
      ),
    },
  ];

  return (
    <Modal
      title={malzeme ? `${malzeme.ad} — Detay` : 'Malzeme Detay'}
      open={open}
      onCancel={onClose}
      footer={null}
      width={950}
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
      ) : (
        <Tabs items={tabItems} defaultActiveKey="alis" />
      )}
    </Modal>
  );
}

export default MalzemeDetayModal;
